import styled from '@emotion/styled';
import React from 'react';
import { Image, Text } from '~/shared/components';
import Flex from '~/shared/components/Flex/Flex';
import { Image as ImageType } from '~/lib/data-contract/salesforce';
import { useUtils } from '~/shared/hooks/useUtils';
import useBasket from '~/features/commerce-api/hooks/useBasket';
import { useTranslation } from '~/shared/utils/translation';

export const ImageWrap = styled.div<{ large?: boolean }>(({ theme, large }) => ({
    height: large ? 96 : 72,
    width: large ? 96 : 72,
    background: theme.colors.productImageBackround,
    img: {
        mixBlendMode: 'multiply',
    },
}));

const Brand = styled.div<{ large?: boolean }>(({ theme, large }) => ({
    ...theme.mixins.useTextStyle('formsLabelActive'),
    fontSize: large ? theme.fontSizes['xs'] : theme.fontSizes['2xs'],
    fontWeight: theme.fontWeights.regular,
    marginBottom: large ? theme.space[2] : 0,
    color: theme.colors.grey90,
}));

const Name = styled.div<{ large?: boolean }>(({ theme, large }) => ({
    fontSize: large ? theme.fontSizes['lg'] : theme.fontSizes['2xs'],
    fontWeight: theme.fontWeights.medium,
    marginBottom: large ? theme.space[2] : 0,
}));

const Content = styled(Flex)(({ theme }) => ({
    padding: theme.space[3],
}));

const PriceWrap = styled.div(({ theme }) => ({
    flexShrink: 0, // This ensures to not wrap price in 2 lines in case of long item description
    padding: `${theme.space[2]} ${theme.space[3]}`,
    border: `1px solid ${theme.colors.brownLight}`,
    borderRadius: theme.sizes.cornerRadius,
    fontSize: '12px',
    marginRight: theme.space[3],
}));

type ComponentProps = {
    brand?: string;
    name?: string;
    quantity?: string | number;
    image?: ImageType | undefined;
    large?: boolean;
    basePrice?: number;
    price?: number | string;
    memberprice?: number;
    showMemberPrice?: boolean;
    unitPrice?: number;
};

export const DigitalProductCard = ({
    brand,
    name,
    quantity,
    large,
    image,
    basePrice,
}: ComponentProps) => {
    const { convertPriceToLocale } = useUtils();
    const { translate } = useTranslation();

    const basket = useBasket();

    return (
        <Flex alignItems="center" justifyContent="space-between">
            <ImageWrap large={large}>
                {image && (
                    <Image
                        layout="fixed"
                        width={large ? 96 : 72}
                        height={large ? 96 : 72}
                        objectFit="cover"
                        src={image.link}
                    />
                )}
            </ImageWrap>
            <Content flex column justifyContent="space-between" alignItems="start">
                <Brand large={large}>{brand}</Brand>
                <Name large={large}>{name}</Name>
                <Flex gap="medium" alignItems="space-between">
                    <Text variant="caption">{convertPriceToLocale(basePrice)}</Text>
                    {large && (
                        <Text variant="caption">
                            {translate('checkout.digitalDelivery.shippingInfo', {
                                email: basket.customerInfo?.email || '',
                            })}
                        </Text>
                    )}
                </Flex>
            </Content>
            <Flex gap="medium" alignItems="space-between">
                <PriceWrap>
                    {quantity} stk {!large && ` x ` + convertPriceToLocale(basePrice)}
                </PriceWrap>
            </Flex>
        </Flex>
    );
};
